import { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/cn';
import type { LockBody, SplitRatio, SplitStrategy } from '../types/api';

/**
 * G.6 — Dataset split picker.
 *
 * Auto-rebalance: when the customer changes any one of the three
 * numeric fields, the OTHER two are adjusted proportionally so the
 * sum stays at 100% (percent mode) or at imageCount (count mode).
 * This matches the Roboflow / Ultralytics pattern customers already
 * know — they don't have to do the "I changed train, now val + test
 * need to add up to 30" math themselves.
 *
 * If the other two are both zero, the customer has effectively asked
 * for "everything in this one bucket" — we honor that without
 * forcing a non-zero default.
 *
 * Strategy radio: stratified (recommended) / random.
 * Seed: 42 default, hidden behind "show advanced".
 * Reset: one click → defaults (80/15/5 stratified seed=42).
 */
interface DatasetSplitPickerProps {
    value: NonNullable<LockBody['split']>;
    onChange: (next: NonNullable<LockBody['split']>) => void;
    imageCount: number;
    isDarkMode?: boolean;
    disabled?: boolean;
}

const DEFAULT_RATIO: SplitRatio = { train: 80, val: 15, test: 5 };
const DEFAULT_SPLIT: NonNullable<LockBody['split']> = {
    ratio: DEFAULT_RATIO,
    strategy: 'stratified',
    seed: 42,
};

/**
 * rebalanceRatio — when the customer changes one field, distribute
 * the delta across the OTHER two so the sum stays at `target`.
 * Distribution is proportional to each other-field's existing weight;
 * if both other fields are zero, the delta lands entirely on the one
 * with no value (split evenly when both are zero).
 *
 * Returns a fresh SplitRatio. Caller passes to onChange.
 */
function rebalanceRatio(
    current: SplitRatio,
    changed: keyof SplitRatio,
    newValue: number,
    target: number,
): SplitRatio {
    const clamped = Math.max(0, Math.min(target, newValue));
    const others = (['train', 'val', 'test'] as const).filter((k) => k !== changed);
    const remainder = Math.max(0, target - clamped);
    const otherSum = current[others[0]] + current[others[1]];

    let a: number;
    let b: number;
    if (otherSum > 0) {
        // Proportional distribution.
        a = Math.round((current[others[0]] / otherSum) * remainder);
        b = remainder - a;
    } else {
        // Both other fields are zero — split evenly.
        a = Math.round(remainder / 2);
        b = remainder - a;
    }

    return {
        train: changed === 'train' ? clamped : (others[0] === 'train' ? a : b),
        val: changed === 'val' ? clamped : (others[0] === 'val' ? a : b),
        test: changed === 'test' ? clamped : (others[0] === 'test' ? a : b),
    };
}

export default function DatasetSplitPicker({
    value,
    onChange,
    imageCount,
    isDarkMode = false,
    disabled = false,
}: DatasetSplitPickerProps) {
    const ratio: SplitRatio = {
        train: value.ratio?.train ?? DEFAULT_RATIO.train,
        val: value.ratio?.val ?? DEFAULT_RATIO.val,
        test: value.ratio?.test ?? DEFAULT_RATIO.test,
    };
    const strategy: SplitStrategy = value.strategy ?? 'stratified';
    const seed = value.seed ?? 42;

    const [mode, setMode] = useState<'percent' | 'count'>('percent');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const update = (patch: Partial<NonNullable<LockBody['split']>>) => {
        onChange({
            ratio: { ...ratio, ...(patch.ratio ?? {}) },
            strategy: patch.strategy ?? strategy,
            seed: patch.seed ?? seed,
        });
    };

    // Target sum: 100 in percent mode, imageCount in count mode.
    const target = mode === 'percent' ? 100 : Math.max(0, imageCount);

    const onRatioField = (field: keyof SplitRatio, raw: string) => {
        const parsed = Number.parseFloat(raw);
        if (Number.isNaN(parsed) || parsed < 0) return;
        const rebalanced = rebalanceRatio(ratio, field, parsed, target);
        update({ ratio: rebalanced });
    };

    // Switching modes — convert the current ratio to the new target so
    // the displayed numbers feel right. Percent → count uses imageCount,
    // count → percent uses 100. Conversion is proportional.
    const switchMode = (next: 'percent' | 'count') => {
        if (next === mode) return;
        const sum = ratio.train + ratio.val + ratio.test;
        const newTarget = next === 'percent' ? 100 : Math.max(0, imageCount);
        if (sum <= 0 || newTarget === 0) {
            setMode(next);
            return;
        }
        const factor = newTarget / sum;
        const t = Math.round(ratio.train * factor);
        const v = Math.round(ratio.val * factor);
        const tt = newTarget - t - v; // absorb rounding into test
        update({ ratio: { train: t, val: v, test: Math.max(0, tt) } });
        setMode(next);
    };

    const handleReset = () => {
        onChange({ ...DEFAULT_SPLIT, ratio: { ...DEFAULT_RATIO } });
        setMode('percent');
    };

    const ratioSum = ratio.train + ratio.val + ratio.test;
    const sumOK = ratioSum === target || (target === 0 && ratioSum === 0);
    const approxFor = (weight: number) =>
        ratioSum > 0 && mode === 'percent' ? Math.round((weight / ratioSum) * imageCount) : 0;

    const headingCls = cn(
        'text-xs font-semibold uppercase tracking-wide',
        isDarkMode ? 'text-gray-400' : 'text-gray-500',
    );
    const subtleCls = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const labelCls = isDarkMode ? 'text-gray-200' : 'text-gray-700';

    return (
        <div
            className={cn(
                'rounded-lg border p-4 space-y-4',
                isDarkMode ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50',
            )}
        >
            {/* Mode toggle + reset */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className={headingCls}>Dataset split</span>
                <div className="flex items-center gap-3 text-sm">
                    <label className={cn('flex items-center gap-1.5 cursor-pointer', labelCls)}>
                        <input
                            type="radio"
                            name="split-mode"
                            checked={mode === 'percent'}
                            onChange={() => switchMode('percent')}
                            disabled={disabled}
                            className="cursor-pointer"
                        />
                        <span>Percentages</span>
                    </label>
                    <label className={cn('flex items-center gap-1.5 cursor-pointer', labelCls)}>
                        <input
                            type="radio"
                            name="split-mode"
                            checked={mode === 'count'}
                            onChange={() => switchMode('count')}
                            disabled={disabled || imageCount === 0}
                            className="cursor-pointer"
                        />
                        <span>Exact counts</span>
                    </label>
                    <button
                        type="button"
                        onClick={handleReset}
                        disabled={disabled}
                        title="Reset to 80 / 15 / 5 stratified seed=42"
                        className={cn(
                            'inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline',
                            isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
                            disabled && 'opacity-60 cursor-not-allowed',
                        )}
                    >
                        <RefreshCw className="h-3 w-3" />
                        Reset
                    </button>
                </div>
            </div>

            {/* Three numeric inputs in a row. */}
            <div className="grid grid-cols-3 gap-3">
                {(['train', 'val', 'test'] as const).map((key) => {
                    const display: Record<typeof key, string> = {
                        train: 'Training',
                        val: 'Validation',
                        test: 'Testing',
                    };
                    return (
                        <label key={key} className="flex flex-col gap-1">
                            <span className={cn('text-xs', labelCls)}>{display[key]}</span>
                            <div className="relative">
                                <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={ratio[key]}
                                    onChange={(e) => onRatioField(key, e.target.value)}
                                    disabled={disabled}
                                    className={cn(
                                        'w-full rounded-md border px-2 py-1.5 text-sm pr-7 focus:outline-none focus:ring-2 focus:ring-primary-500',
                                        isDarkMode
                                            ? 'bg-gray-800 border-gray-700 text-gray-100'
                                            : 'bg-white border-gray-300 text-gray-900',
                                        disabled && 'opacity-60 cursor-not-allowed',
                                    )}
                                />
                                <span
                                    className={cn(
                                        'absolute right-2 top-1/2 -translate-y-1/2 text-xs pointer-events-none',
                                        subtleCls,
                                    )}
                                >
                                    {mode === 'percent' ? '%' : ''}
                                </span>
                            </div>
                            {mode === 'percent' && imageCount > 0 && (
                                <span className={cn('text-[11px]', subtleCls)}>
                                    ≈ {approxFor(ratio[key])} images
                                </span>
                            )}
                        </label>
                    );
                })}
            </div>

            {/* Sum indicator — auto-rebalance keeps this at target, but show
                it explicitly so the customer sees the constraint. */}
            <div className="flex items-center justify-between text-xs">
                <span className={subtleCls}>
                    Total: {' '}
                    <span className={cn(
                        'tabular-nums font-medium',
                        sumOK
                            ? (isDarkMode ? 'text-emerald-300' : 'text-emerald-700')
                            : (isDarkMode ? 'text-amber-300' : 'text-amber-700'),
                    )}>
                        {ratioSum}{mode === 'percent' ? '%' : ''}
                    </span>
                    {target > 0 && (
                        <>
                            {' '}of {target}{mode === 'percent' ? '%' : ' images'}
                        </>
                    )}
                </span>
                {sumOK && (
                    <span className={cn(
                        'inline-flex items-center gap-1',
                        isDarkMode ? 'text-emerald-300' : 'text-emerald-700',
                    )}>
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        balanced
                    </span>
                )}
            </div>

            {/* Strategy radio */}
            <div className="space-y-1.5">
                <span className={headingCls}>Strategy</span>
                <div className="flex flex-col gap-1.5 text-sm">
                    <label className={cn('flex items-start gap-2 cursor-pointer', labelCls)}>
                        <input
                            type="radio"
                            name="split-strategy"
                            checked={strategy === 'stratified'}
                            onChange={() => update({ strategy: 'stratified' })}
                            disabled={disabled}
                            className="mt-0.5 cursor-pointer"
                        />
                        <span>
                            <span>Stratified by label</span>
                            <span className={cn('ml-1.5 text-xs', subtleCls)}>
                                (recommended — distributes every label proportionally)
                            </span>
                        </span>
                    </label>
                    <label className={cn('flex items-start gap-2 cursor-pointer', labelCls)}>
                        <input
                            type="radio"
                            name="split-strategy"
                            checked={strategy === 'random'}
                            onChange={() => update({ strategy: 'random' })}
                            disabled={disabled}
                            className="mt-0.5 cursor-pointer"
                        />
                        <span>
                            <span>Random</span>
                            <span className={cn('ml-1.5 text-xs', subtleCls)}>
                                (faster — rare labels may end up in only one set)
                            </span>
                        </span>
                    </label>
                </div>
            </div>

            {/* Advanced — seed control */}
            <div>
                <button
                    type="button"
                    onClick={() => setShowAdvanced((v) => !v)}
                    className={cn(
                        'text-xs underline-offset-2 hover:underline',
                        isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
                    )}
                    disabled={disabled}
                >
                    {showAdvanced ? 'Hide advanced' : 'Show advanced'}
                </button>
                {showAdvanced && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                        <span className={cn('text-xs', labelCls)}>Seed</span>
                        <input
                            type="number"
                            value={seed}
                            onChange={(e) => {
                                const n = Number.parseInt(e.target.value, 10);
                                if (!Number.isNaN(n)) update({ seed: n });
                            }}
                            disabled={disabled}
                            className={cn(
                                'rounded-md border px-2 py-1 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-primary-500',
                                isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-gray-100'
                                    : 'bg-white border-gray-300 text-gray-900',
                                disabled && 'opacity-60 cursor-not-allowed',
                            )}
                        />
                        <span className={cn('text-[11px]', subtleCls)}>
                            Same seed produces the same split — change to try a different one.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export function useDebouncedValue<T>(value: T, delayMs = 300): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}
